const hiddenClass = 'selling-plan-theme-integration--hidden';

class SellingPlansWidget extends HTMLElement {
  constructor() {
    super();

    this.variantIdObserver = null;

    this.performanceObserver = null;
    this.listenersAbortController = null;

    this.onPageShow = this.onPageShow.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.listenersAbortController = new AbortController();

    window.addEventListener('pageshow', this.onPageShow);

    this.enablePerformanceObserver();
    this.appendSellingPlanInputs();
    this.syncCurrentState();
    this.updateSellingPlanInputsValues();
    this.updatePricesForVariants();
    this.updatePricesForPlan();
    this.listenToVariantChange();
    this.listenToQuantityChange();
    this.listenToSellingPlanFormRadioButtonChange();
    this.listenToSellingPlanFormOptionChange();
  }

  disconnectedCallback() {
    if (!this._initialized) return;

    this.variantIdObserver?.disconnect();

    window.removeEventListener('pageshow', this.onPageShow);
    this.performanceObserver?.disconnect();
    this.listenersAbortController?.abort();

    this._initialized = false;
  }

  onPageShow() {
    this.syncCurrentState();
  }

  syncCurrentState() {
    this.handleSellingPlanFormVisibility();
    this.updatePricesForPlan();
    this.updatePricesForVariants();
    this.updateSellingPlanInputsValues();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    const form = this.addToCartForms[1] || this.addToCartForms[0];
    const idElement = form?.querySelector(`input[name="id"]`) || form?.querySelector(`select[name="id"]`);

    return idElement || null;
  }

  get visibleSellingPlanForm() {
    const variantId = this.variantIdInput?.value;
    if (!variantId) return null;

    return this.shopifySection?.querySelector(
      `section[data-variant-id^="${variantId}"]`)
      ?? null;
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption?.getAttributeNames().includes('disabled') || false;
  }

  get sellingPlanInput() {
    return this.shopifySection?.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection?.querySelectorAll('[action*="/cart/add"]') || [];
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput?.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const variantId = this.variantIdInput?.value;
    if (!variantId) return null;

    const sellingPlanFormForSelectedVariant = this.shopifySection?.querySelector(
      `section[data-variant-id="${variantId}"]`,
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection?.querySelectorAll(
        `.selling-plan-theme-integration:not([data-variant-id="${variantId}"])`,
      ) || [];
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
    this.updatePricesForVariants();
    this.updatePricesForPlan();
  }

  updatePricesForVariants() {
    const variantInputElementList = document.querySelectorAll('[data-one-time-discount]');
    variantInputElementList.forEach(input => {
      const label = input.nextElementSibling;
      const priceElement = label?.querySelector('.quantity-picker__price');
      const badgeElement = label?.querySelector('.badge-discount__wrapper');
      if (!priceElement || !badgeElement) return;

      const currentPlan = this.visibleSellingPlanForm?.querySelector('input[type="radio"]:checked');
      const price = parseInt(input.dataset.price, 10);
      const discountMultiplier = currentPlan?.hasAttribute('data-selling-plan-checked') ?
        parseFloat(input.dataset.subscriptionDiscount) : parseFloat(input.dataset.oneTimeDiscount);

      const salePrice = price * discountMultiplier;

      priceElement.textContent = Shopify.formatMoney(salePrice);
      price == salePrice ? label?.classList.remove('quantity-picker__option--show-discount') : label?.classList.add('quantity-picker__option--show-discount');

      const discount = Math.round(100 - (discountMultiplier * 100));

      const badgeTextElement = badgeElement.querySelector('.badge-discount__text');
      if (!badgeTextElement) return;
      badgeTextElement.textContent = window.purchaseOptionsStrings.saveWithDiscount.replace('[discount]', discount);
    })

  }

  updatePricesForPlan() {
    const currentVariant = document.querySelector('[data-one-time-discount]:checked');
    const price = parseInt(currentVariant?.dataset.price, 10);
    const quantity = parseInt(currentVariant?.value);

    const purchaseOptionElementList = this.visibleSellingPlanForm?.querySelectorAll('.purchase-option__wrapper');

    purchaseOptionElementList.forEach(purchaseOption => {
      const priceElement = purchaseOption.querySelector('.plan-price');
      const badgeElement = purchaseOption.querySelector('.plan-price__badge');
      if (!priceElement || !badgeElement) return;

      const regularPriceElement = priceElement.querySelector('.plan-price__regular');
      const fullPriceElement = priceElement.querySelector('.plan-price__full');
      const salePriceElement = priceElement.querySelector('.plan-price__sale');
      if (!regularPriceElement || !fullPriceElement || !salePriceElement) return;

      const bulkDiscount = parseFloat(currentVariant.dataset.oneTimeDiscount);
      const subscriptionDiscount = parseFloat(currentVariant.dataset.subscriptionDiscount);
      const discountMultiplier = purchaseOption?.previousElementSibling?.hasAttribute('data-selling-plan-checked') ?
        parseFloat(currentVariant.dataset.subscriptionDiscount) : parseFloat(currentVariant.dataset.oneTimeDiscount);

      const salePrice = price * discountMultiplier;
      const fullPrice = Shopify.formatMoney(salePrice * quantity);

      regularPriceElement.textContent = Shopify.formatMoney(price);
      fullPriceElement.textContent = window.purchaseOptionsStrings.fullPrice.replace('[full_price]', fullPrice);

      const discount = Math.round(100 - (discountMultiplier * 100));

      if (price == salePrice) {
        priceElement.classList.add('plan-price--show-regular');
        priceElement.classList.remove('plan-price--show-sale');
        badgeElement.classList.remove('plan-price__badge--badge-sale');
        if (priceElement?.hasAttribute('data-subscription')) {
          badgeElement.textContent = window.purchaseOptionsStrings.saveWithoutDiscount;
        } else {
          badgeElement.textContent = '';
        }

      } else {
        priceElement.classList.add('plan-price--show-sale');
        priceElement.classList.remove('plan-price--show-regular');
        salePriceElement.innerHTML = Shopify.formatMoney(salePrice);
        badgeElement.textContent = window.purchaseOptionsStrings.saveWithDiscount.replace('[discount]', discount);
        badgeElement.classList.add('plan-price__badge--badge-sale');
      }
    });
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */

  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput?.tagName === 'INPUT') {
      this.variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange(mutationRecord.target.value);
        });
      });

      this.variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToQuantityChange() {
    const signal = this.listenersAbortController?.signal;
    const quantityInputs = this.shopifySection?.querySelectorAll('input[name="quantity-picker"]') || [];

    quantityInputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        if (!event.target.checked) return;
        this.updatePricesForVariants();
        this.updatePricesForPlan();
      }, { signal });
    });
  }

  listenToAddToCartForms() {
    const signal = this.listenersAbortController?.signal;

    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      }, { signal });
    });
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption?.dataset.sellingPlanGroupId}_allocation_price`,
    );
  }

  /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */

  get sellingPlanInputs() {
    return this.shopifySection?.querySelectorAll('.selected-selling-plan-id');
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    const checkedInput = this.visibleSellingPlanForm?.querySelector('input:checked');
    return checkedInput?.hasAttribute('data-selling-plan-checked')
      ? this.visibleSellingPlanForm.querySelector('option:checked')
      : checkedInput;
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
    this.updatePricesForVariants();

    if (this.selectedPurchaseOption.closest('[data-purchase-option="One-time"]')) {
      this.shopifySection?.querySelector('[data-subscription-terms]')?.classList.add('hidden');
    } else {
      this.shopifySection?.querySelector('[data-subscription-terms]')?.classList.remove('hidden');
    }
  }

  listenToSellingPlanFormRadioButtonChange() {
    const signal = this.listenersAbortController?.signal;

    this.visibleSellingPlanForm
      ?.querySelectorAll('input[type="radio"]')
      .forEach((radio) => {
        radio.addEventListener('change', (event) => {
          this.updatePricesForVariants();
          this.updatePricesForPlan();
          if (radio.hasAttribute('data-selling-plan-checked'))
            this.handleRadioButtonChange(event.target.querySelector("option:checked"));
          else
            this.handleRadioButtonChange(event.target);
        }, { signal });
      });
  }

  listenToSellingPlanFormOptionChange() {
    const signal = this.listenersAbortController?.signal;

    this.visibleSellingPlanForm
      ?.querySelectorAll('select')
      .forEach((select) => {
        select.addEventListener('change', (event) => {
          const checkedInput = this.visibleSellingPlanForm?.querySelector('input:checked');
          if (checkedInput?.hasAttribute('data-selling-plan-checked')) {
            this.handleRadioButtonChange(event.target.querySelector("option:checked"));
          }
        }, { signal });
      });
  }

  enablePerformanceObserver() {
    this.performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes('variant') || url.search.includes('variants')) {
          this.updatePricesForVariants();
          this.updatePricesForPlan();
        }
      });
    });

    this.performanceObserver.observe({ entryTypes: ['resource'] });
  }
}

if (!customElements.get('selling-plan-widget')) {
  customElements.define('selling-plan-widget', SellingPlansWidget);
}